import * as React from "react";
import { Check, ChevronsUpDown, Loader2, Plus, Settings } from "lucide-react";
import { cn } from "@repo/utils";
import {
  Button,
  useToast,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui";
import type { Wholesaler } from "@/lib/types/inventory";
import { getWholesalers, createWholesaler } from "@/lib/api/wholesalers";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";

export interface WholesalerComboboxProps {
  value?: string;
  onSelect?: (wholesaler: Wholesaler | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  displayLabel?: string;
  onManageClick?: () => void;
  refreshTrigger?: number;
  "data-testid"?: string;
}

export function WholesalerCombobox({
  value,
  onSelect,
  placeholder = "Select wholesaler...",
  searchPlaceholder = "Search or create wholesaler...",
  disabled = false,
  className,
  displayLabel,
  onManageClick,
  refreshTrigger,
  "data-testid": dataTestId,
}: WholesalerComboboxProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [wholesalers, setWholesalers] = React.useState<Wholesaler[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [selectedWholesaler, setSelectedWholesaler] =
    React.useState<Wholesaler | null>(null);

  // Load wholesalers when component mounts or popover opens
  const loadWholesalers = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getWholesalers();
      setWholesalers(data.data.wholesalers || []);
    } catch (error) {
      console.error("Load error:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to load wholesalers. Please try again.",
        variant: "destructive",
      });
      setWholesalers([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Load wholesalers on mount and when refreshTrigger changes
  React.useEffect(() => {
    loadWholesalers();
  }, [loadWholesalers, refreshTrigger]);

  // Set selected wholesaler based on value prop
  React.useEffect(() => {
    if (value && wholesalers.length > 0) {
      const found = wholesalers.find((w) => w.id === value);
      if (found) {
        setSelectedWholesaler(found);
      }
    } else if (!value) {
      setSelectedWholesaler(null);
    }
  }, [value, wholesalers]);

  // Filter wholesalers based on search query
  const filteredWholesalers = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return wholesalers;
    }

    const query = searchQuery.toLowerCase();
    return wholesalers.filter((w) => w.name.toLowerCase().includes(query));
  }, [wholesalers, searchQuery]);

  // Check if search query is a new wholesaler name
  const isNewWholesaler =
    searchQuery.trim().length > 0 &&
    !wholesalers.some(
      (w) => w.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );

  const handleSelect = (wholesaler: Wholesaler) => {
    setSelectedWholesaler(wholesaler);
    onSelect?.(wholesaler);
    setSearchQuery("");
    setOpen(false);
  };

  const handleCreateNew = async () => {
    if (!searchQuery.trim()) return;

    setIsCreating(true);
    try {
      const data = await createWholesaler({ name: searchQuery.trim() });

      const newWholesaler = data.data.wholesaler;

      // Add to list and select
      setWholesalers((prev) =>
        [...prev, newWholesaler].sort((a, b) => a.name.localeCompare(b.name))
      );
      setSelectedWholesaler(newWholesaler);
      onSelect?.(newWholesaler);
      setSearchQuery("");
      setOpen(false);

      toast({
        title: "Wholesaler Created",
        description: `"${newWholesaler.name}" has been added to your list.`,
        variant: "success",
      });
    } catch (error) {
      // Capture name before approval flow (searchQuery may change)
      const capturedName = searchQuery.trim();
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {
            if (!capturedName) return;
            const retryData = await createWholesaler({ name: capturedName });
            const retryWholesaler = retryData.data.wholesaler;
            setWholesalers((prev) =>
              [...prev, retryWholesaler].sort((a, b) =>
                a.name.localeCompare(b.name)
              )
            );
            setSelectedWholesaler(retryWholesaler);
            onSelect?.(retryWholesaler);
            setSearchQuery("");
            setOpen(false);
            toast({
              title: "Wholesaler Created",
              description: `"${retryWholesaler.name}" has been added to your list.`,
              variant: "success",
            });
          },
          actionDescription: "Create wholesaler",
        })
      )
        return;
      console.error("Create error:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create wholesaler",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchQuery("");
    }
  };

  const handleManageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    onManageClick?.();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <span className="truncate">
            {displayLabel || selectedWholesaler?.name || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[300px] max-w-[500px] p-0"
        align="start"
      >
        <div className="flex flex-col">
          {/* Search Input */}
          <div className="flex items-center border-b border-border px-3">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isNewWholesaler) {
                  e.preventDefault();
                  handleCreateNew();
                }
              }}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>

          {/* Results List */}
          <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading...
                </span>
              </div>
            )}

            {!isLoading && (
              <>
                {/* Create New Option */}
                {isNewWholesaler && (
                  <div className="border-b border-border p-1">
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      disabled={isCreating}
                      className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary disabled:pointer-events-none disabled:opacity-50"
                    >
                      {isCreating ? (
                        <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                      ) : (
                        <Plus className="mr-2 h-4 w-4 shrink-0" />
                      )}
                      <div className="flex flex-col items-start">
                        <span className="font-medium">
                          Create &quot;{searchQuery.trim()}&quot;
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Add new wholesaler
                        </span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Existing Wholesalers */}
                {filteredWholesalers.length > 0 && (
                  <div className="p-1">
                    {filteredWholesalers.map((wholesaler) => (
                      <button
                        key={wholesaler.id}
                        type="button"
                        onClick={() => handleSelect(wholesaler)}
                        className={cn(
                          "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary",
                          selectedWholesaler?.id === wholesaler.id &&
                            "bg-primary-soft text-primary"
                        )}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0",
                            selectedWholesaler?.id === wholesaler.id
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col items-start flex-1">
                          <span className="font-medium">{wholesaler.name}</span>
                          {wholesaler.usage_count !== undefined &&
                            wholesaler.usage_count > 0 && (
                              <span className="text-xs text-muted-foreground">
                                Used {wholesaler.usage_count} time
                                {wholesaler.usage_count !== 1 ? "s" : ""}
                              </span>
                            )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {!isNewWholesaler &&
                  filteredWholesalers.length === 0 &&
                  searchQuery.trim().length > 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No wholesalers found
                    </div>
                  )}

                {!isNewWholesaler &&
                  filteredWholesalers.length === 0 &&
                  searchQuery.trim().length === 0 &&
                  wholesalers.length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No wholesalers yet. Type to create one.
                    </div>
                  )}
              </>
            )}
          </div>

          {/* Manage Button */}
          {onManageClick && !isLoading && (
            <div className="border-t border-border p-1">
              <button
                type="button"
                onClick={handleManageClick}
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary"
              >
                <Settings className="mr-2 h-4 w-4 shrink-0" />
                <span>Manage Wholesalers</span>
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
